import { Checkbox, Flex, Popover, Radio, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import pluralize from 'pluralize';
import {
  DetectionState,
  EntityCategory,
  selectConflictFields,
  selectExistingFieldsForConflict,
  selectSelectedConflictIndex,
  selectSelectedFieldNames,
  useDetectedEntitiesStore,
} from '../stores/useDetectedEntitiesStore';
import { DataInput, renderValue } from './DataInput';
import { useDisclosure } from '@mantine/hooks';
import {
  camelCaseOrSnakeCaseToWords,
  fieldsToHide,
} from 'src/data-capture/merge-review/ReviewHelperFunctions';
import useDynamicNonFinancialData from 'src/non-financial-data/hooks/useDynamicNonFinancialData';
import { FieldTypeMetadata } from 'src/data-capture/types';

type ConflictOptionProps = {
  category: EntityCategory;
  detection: DetectionState;
  index: number;
};

export default function ConflictOption({ category, detection, index }: ConflictOptionProps) {
  const fields = useDetectedEntitiesStore(state =>
    selectExistingFieldsForConflict(state, category, detection.id, index),
  );
  const selectedConflictIndex = useDetectedEntitiesStore(state =>
    selectSelectedConflictIndex(state, category, detection.id),
  );
  const selectConflictIndex = useDetectedEntitiesStore(state => state.selectConflictIndex);
  const isSelected = selectedConflictIndex === index;
  const selectedFieldNames = useDetectedEntitiesStore(state =>
    selectSelectedFieldNames(state, category, detection.id),
  );
  const toggleConflictField = useDetectedEntitiesStore(state => state.toggleConflictField);
  const { nonFinancialData } = useDynamicNonFinancialData();
  const entityTypeMetadata = nonFinancialData?.ui.entitiesMetadata[detection.entityType];
  const showRadio = detection.conflicts.length > 0;
  const entityName = pluralize.singular(detection.entityLabel.toLowerCase());

  return (
    <Flex direction="column" gap="xl" data-testid="conflict-option">
      <Flex
        gap="xs"
        align="center"
        onClick={showRadio ? () => selectConflictIndex(category, detection.id, index) : undefined}
      >
        {showRadio && <Radio.Indicator checked={isSelected} />}
        <Text fw={700} size="sm" c="gray.10">
          Existing {entityName} {index + 1}
        </Text>
        <ConflictInfo category={category} detection={detection} index={index} />
      </Flex>
      <Flex direction="column" gap="md">
        {fields.map(({ key, value, metadata, isDetected }) => {
          const resolvedMetadata: FieldTypeMetadata | undefined =
            metadata ?? entityTypeMetadata?.[key];
          const labelColour = isDetected ? 'gray.10' : 'gray.7';
          return (
            <Flex key={key} gap="xs" align="center">
              {isSelected && isDetected && (
                <Checkbox
                  size="xs"
                  checked={selectedFieldNames.includes(key)}
                  onChange={() => toggleConflictField(category, detection.id, key)}
                />
              )}
              <Flex gap="xxxs">
                <Text fw={500} size="sm" c={labelColour}>
                  {resolvedMetadata?.label || camelCaseOrSnakeCaseToWords(key)}:
                </Text>
                {isDetected ? (
                  <DataInput editable={false} highlight="existing" metadata={resolvedMetadata}>
                    {value}
                  </DataInput>
                ) : (
                  <Text size="sm" c={labelColour}>
                    {renderValue(value, resolvedMetadata)}
                  </Text>
                )}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
}

function ConflictInfo({ category, detection, index }: ConflictOptionProps) {
  const [opened, { close, open }] = useDisclosure(false);
  const fields = useDetectedEntitiesStore(state =>
    selectConflictFields(state, category, detection.id, index),
  );
  const entityName = pluralize.singular(detection.entityLabel.toLowerCase());

  return (
    <Popover width={300} position="right" shadow="md" opened={opened}>
      <Popover.Target>
        <IconInfoCircle
          size={16}
          color="black"
          style={{ cursor: 'pointer' }}
          onMouseEnter={open}
          onMouseLeave={close}
          strokeWidth={2}
          data-testid="conflict-info-icon"
        />
      </Popover.Target>
      <Popover.Dropdown
        p="md"
        bg="rgba(0,0,0,0.8)"
        style={{ backdropFilter: 'blur(44px)', borderRadius: 16, pointerEvents: 'none' }}
        bd="1.5px solid var(--mantine-color-gray-3)"
      >
        <Flex direction="column" gap="xxs">
          <Text fw={700} size="sm" c="white" opacity={0.5}>
            Existing {entityName} {index + 1}
          </Text>
          <Flex direction="column" gap="xs">
            {fields
              .filter(f => !fieldsToHide.includes(f.key))
              .map(({ key, value, metadata }) => {
                return (
                  <Flex key={key} gap="xxs">
                    <Text size="md" c="white" fw={600}>
                      {metadata?.label || camelCaseOrSnakeCaseToWords(key)}:
                    </Text>
                    <Text size="md" fw={400} c="white">
                      {renderValue(value, metadata)}
                    </Text>
                  </Flex>
                );
              })}
          </Flex>
        </Flex>
      </Popover.Dropdown>
    </Popover>
  );
}
